/**
 * Parsing for `document_loads.normalization_warnings` (#7213).
 *
 * The worker writes one warning per claimant whose net-new stop-loss spend could not
 * file because the target vendor's open SL claim is frozen (submitted on a track or
 * carrying an open task). Format, from `SLCHeldSpend.Warning()` in coronis-apps:
 *
 *   slc_bucket_occupied: claimant <uuid> policy <uuid|none> vendor <uuid|unassigned> reason <code> — …
 *
 * `reason` (why the spend was held) was added by the !4803 review; warnings written
 * before it have no reason and parse with `reason` undefined. Since coronis-apps #7215
 * the only reason written is `concurrent_insert`: nothing is frozen — another pass for
 * the same claimant filed the stop-loss claim first. The freeze reasons are historical.
 *
 * Anything that doesn't match is kept as plain text so a future warning kind still
 * shows up instead of being dropped.
 */

/** Why the spend was held. Mirrors coronis-apps `HeldReason*` (types/loader.go). */
export type HeldSpendReason =
  | "aura_submitted"
  | "carrier_submitted"
  | "open_task"
  | "unknown"
  | "concurrent_insert"
  // Feature #3502 (#7340): the claimant's deductible review is open.
  | "deductible_review_pending";

const KNOWN_REASONS: readonly HeldSpendReason[] = [
  "aura_submitted",
  "carrier_submitted",
  "open_task",
  "unknown",
  "concurrent_insert",
  "deductible_review_pending",
];

export interface HeldSpendWarning {
  claimantId: string;
  policyId?: string;
  vendorId?: string;
  /** Undefined for warnings recorded before the reason existed. Unrecognised codes map to "unknown". */
  reason?: HeldSpendReason;
}

/** A claimant whose post-processing a load skipped because another pass held its fence (#7215). */
export interface FenceSkippedWarning {
  claimantId: string;
  /** How long the load waited before skipping, as the worker rendered it (e.g. "1h0m0s"). */
  waited?: string;
}

/** Claim lines this load wrote with a paid amount and no paid date (#7380). */
export interface NoPaidDateWarning {
  lines: number;
}

export interface ParsedLoadWarnings {
  heldSpend: HeldSpendWarning[];
  fenceSkipped: FenceSkippedWarning[];
  /** Present when the load recorded lines without a paid date (#7380). */
  noPaidDate?: NoPaidDateWarning;
  other: string[];
}

const HELD_SPEND = /^slc_bucket_occupied: claimant (\S+) policy (\S+) vendor (\S+)(?: reason (\S+))?/;
// FenceSkippedClaimant.Warning() in coronis-apps (#7215):
//   load_fence_skipped: claimant <uuid> waited <duration> — …
const FENCE_SKIPPED = /^load_fence_skipped: claimant (\S+)(?: waited (\S+))?/;
// types.NoPaidDateWarning() in coronis-apps (#7380):
//   claim_lines_no_paid_date: lines <n> — …
const NO_PAID_DATE = /^claim_lines_no_paid_date: lines (\d+)/;

export const parseLoadWarnings = (warnings: string[] | null | undefined): ParsedLoadWarnings => {
  const parsed: ParsedLoadWarnings = { heldSpend: [], fenceSkipped: [], other: [] };
  for (const w of warnings ?? []) {
    const noDate = NO_PAID_DATE.exec(w);
    if (noDate) {
      const lines = Number(noDate[1]);
      // One per load; a second entry (should not happen) adds up rather than hides.
      parsed.noPaidDate = { lines: (parsed.noPaidDate?.lines ?? 0) + lines };
      continue;
    }
    const skipped = FENCE_SKIPPED.exec(w);
    if (skipped) {
      parsed.fenceSkipped.push({ claimantId: skipped[1], waited: skipped[2] });
      continue;
    }
    const m = HELD_SPEND.exec(w);
    if (!m) {
      if (w.trim()) parsed.other.push(w);
      continue;
    }
    parsed.heldSpend.push({
      claimantId: m[1],
      policyId: m[2] === "none" ? undefined : m[2],
      vendorId: m[3] === "unassigned" ? undefined : m[3],
      reason:
        m[4] === undefined
          ? undefined
          : KNOWN_REASONS.includes(m[4] as HeldSpendReason)
            ? (m[4] as HeldSpendReason)
            : "unknown",
    });
  }
  return parsed;
};

/**
 * What releases held spend, per reason. Since coronis-apps #7215 two reasons are
 * written. `concurrent_insert`: another load (or recompute) for the same claimant
 * filed the stop-loss claim first, nothing is frozen, and this spend nets into that
 * claim on the claimant's next load or recompute. `deductible_review_pending`
 * (Feature #3502): an open deductible review holds the claimant's stop-loss claims
 * until someone resolves it. The freeze reasons are historical:
 * an open task released when closed (pre-#7312); since migration 000230 (#7236) an
 * Aura- or carrier-submitted claim no longer holds its bucket. Warnings recorded before
 * the reason existed (undefined) keep the generic wording.
 */
export const HELD_REASON_TEXT: Record<HeldSpendReason | "legacy", string> = {
  concurrent_insert:
    "Another load or recompute for this claimant filed the stop-loss claim first. This spend is counted the next time the claimant is loaded or recomputed.",
  open_task: "Open task on the claim. Files on the next load after the task is closed.",
  aura_submitted: "Sent for Aura funding. Files on the next load for this claimant.",
  carrier_submitted: "Submitted to the carrier. Files on the next load for this claimant.",
  unknown:
    "Claim is frozen. Files on the next load after it's released (task closed, sent for funding or to the carrier, or moved out of New).",
  legacy: "Files on the next load after the task is closed or the claim moves out of New.",
  deductible_review_pending:
    "The claimant's deductible needs review (an ambiguous conditional laser or a change after submission). Files once someone resolves the review on the claimant page.",
};
